/** biome-ignore-all lint/style/noRestrictedImports: not needed */

import { useTranslation } from '@unifyapps/i18n/client';
import {
  getGetUnopenedNotificationsCountQueryKey,
  useMarkAsOpen,
} from '@unifyapps/network/generated/notification-rest-api/notification-rest-api';
import { useQueryClient } from '@unifyapps/network/react-query';
import { ToolbarVariant } from '@unifyapps/table/types';
import { Box } from '@unifyapps/ui/components/Box';
import { Loader } from '@unifyapps/ui/components/Loader';
import { Stack } from '@unifyapps/ui/components/Stack';
import clsx from 'clsx';
import { type JSX, Suspense, useEffect, useMemo, useState } from 'react';
import { useLatest } from 'react-use';
import { useNotifications } from '../hooks/useNotifications';
import { useResolvedNotifications } from '../hooks/useResolvedNotifications';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { Filters } from './Filters';
import { Notifications } from './Notifications';

const unOpenedNotificationsCountQueryKey = getGetUnopenedNotificationsCountQueryKey();

type Props = {
  userId: number;
  tags?: { id: string; label: string; hidden?: boolean }[];
  scheme?: 'light' | 'dark';
  toolbarVariant?: ToolbarVariant;
};

export function NotificationsContainer({ userId, tags, scheme, toolbarVariant }: Props) {
  const { t } = useTranslation(['common']);

  const queryClient = useQueryClient();

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [filter, setFilter] = useState<string>('ALL');
  const [unread, setUnread] = useState<boolean>(false);

  const { notifications, fetchNextPage, isLoading, refetch, error, invalidate } = useNotifications({
    userId,
    filter,
    unread,
  });

  const { mutate: markAsOpen } = useMarkAsOpen({
    mutation: {
      onSuccess: () => {
        // Invalidate the unopened notifications count query so that the dot icon on the bell icon is updated
        void queryClient.invalidateQueries({ queryKey: unOpenedNotificationsCountQueryKey });
      },
    },
  });

  const notificationsRef = useLatest(notifications);
  const markAsOpenRef = useLatest(markAsOpen);
  // biome-ignore lint/correctness/useExhaustiveDependencies: not needed
  useEffect(
    () => () => {
      if (notificationsRef.current[0]?.id && notificationsRef.current[0].createdTime) {
        // Mark the most recent notification as opened on drawer close
        markAsOpenRef.current({
          params: {
            lastOpenedId: notificationsRef.current[0].id,
            lastOpenedTime: notificationsRef.current[0].createdTime,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only to be ran on unmount, used refs inside
    [],
  );

  const filterOptions = useMemo(
    // Todo: To be updated based on actual tags once design is finalised (https://unifyapps.monday.com/boards/1434760518/pulses/1589185803)
    () =>
      tags?.filter((tag) => !tag.hidden).map((tag) => ({ id: tag.id, name: tag.label })) ?? [
        { id: 'ALL', name: t('common:All') },
        { id: 'Alerts', name: t('common:Alerts') },
        { id: 'Mentions', name: t('common:Mentions') },
      ],
    [t, tags],
  );

  const { notifications: resolvedNotifications } = useResolvedNotifications({ notifications });

  let contentEl: JSX.Element | null = null;

  if (isLoading) {
    contentEl = (
      <Box className="flex size-full items-center justify-center">
        <Loader size="sm" variant="circle" />
      </Box>
    );
  } else if (!resolvedNotifications?.length) {
    contentEl = (
      <Suspense fallback={null}>
        <EmptyState />
      </Suspense>
    );
  } else if (error) {
    contentEl = <ErrorState />;
  } else {
    contentEl = (
      <Notifications
        fetchNextPage={fetchNextPage}
        items={resolvedNotifications}
        onMarkAsRead={invalidate}
        refetch={refetch}
        scrollParent={container ?? undefined}
      />
    );
  }

  return (
    // (48+1)px, 1px for border + 48px for notificationspanel header
    <Stack className="h-[calc(100%-49px)]">
      <Filters
        filter={filter}
        filterOptions={filterOptions}
        scheme={scheme}
        setFilter={setFilter}
        setUnread={setUnread}
        toolbarVariant={toolbarVariant}
        unread={unread}
      />
      <Stack
        className={clsx('px-md pb-md bg-primary flex-1 min-h-0 overflow-y-auto')}
        direction="column"
        ref={setContainer}
      >
        {contentEl}
      </Stack>
    </Stack>
  );
}
